// Componentes compartidos CRM v3 — los consumen CRMMobile y CRM (desktop).
// Una sola fuente de verdad para: burbujas auto, timeline automatizaciones,
// drawer ficha 360, sheet quick actions, wizard nuevo chat, AI suggestions.

import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { sugerirRespuesta } from '../../lib/crmApi'

// ─────────── Paleta + helpers ───────────
export const C = {
  navy: '#1A3461', navy2: '#0E2548',
  teal: '#00A8B5', tealDark: '#0E7A82',
  bone: '#F8F6F1', boneDark: '#F1EFE8',
  text: '#0F1F3D', muted: '#7B8AA8',
  line: 'rgba(15,31,61,.10)',
  red: '#E24B4A', redDark: '#A32D2D',
  green: '#3B6D11', greenSoft: '#97C459',
  amber: '#BA7517', amberSoft: '#FAC775',
}

export function classNombre(nombre) {
  return (nombre || '?').trim().split(/\s+/).slice(0,2).map(s => s[0]?.toUpperCase()).join('') || '?'
}
export function gradAvatar(seed = 0) {
  const palette = [
    ['#1A3461','#0E2548'], ['#00A8B5','#0E7A82'], ['#3B6D11','#27500A'],
    ['#993C1D','#712B13'], ['#534AB7','#3C3489'], ['#185FA5','#0C447C'],
    ['#D85A30','#993C1D'], ['#3B6D11','#173404'],
  ]
  const [a,b] = palette[Math.abs(seed||0) % palette.length]
  return `linear-gradient(135deg, ${a}, ${b})`
}
export function fmtHora(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const hh = String(d.getHours()).padStart(2,'0')
  const mm = String(d.getMinutes()).padStart(2,'0')
  return `${hh}:${mm}`
}

// ─────────── Hook: traer automatizaciones de un cliente ───────────
export function useAutomatizacionesCliente(clienteId) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!clienteId) { setItems([]); return }
    let alive = true
    setLoading(true)
    supabase.from('automatizaciones_log')
      .select('id, tipo, template_name, template_params, estado, enviado_at, error_msg, origen_tabla, origen_id, created_at')
      .eq('target_user_id', clienteId)
      .order('created_at', { ascending: false })
      .limit(80)
      .then(({ data }) => { if (alive) { setItems(data || []); setLoading(false) } })
    return () => { alive = false }
  }, [clienteId])
  return { items, loading }
}

// ─────────── Burbuja de mensaje automático ───────────
// Visualmente DISTINTA al humano: centrada, borde dashed teal, badge con el tipo de evento.
export function AutoBubble({ msg, automation, onAdelantar, onCancelar, onReintentar }) {
  const tipo = automation?.tipo || msg.template_name || 'auto'
  const tipoIcon = TIPO_ICON[tipo] || 'sparkles'
  const esError = msg.estado === 'failed' || automation?.estado === 'failed'
  const esProgramada = !msg.sent_at && (automation?.estado === 'pendiente' || automation?.estado === 'programada')

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', margin:'6px 0' }}>
      <span style={{
        background: esError ? 'rgba(226,75,74,.12)' : 'rgba(0,168,181,.12)',
        color: esError ? C.redDark : C.tealDark,
        border: `0.5px solid ${esError ? 'rgba(226,75,74,.35)' : 'rgba(0,168,181,.35)'}`,
        fontSize: 10, padding: '2px 8px 2px 6px', borderRadius: 999, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 4,
      }}>
        <Icon name={tipoIcon} size={11} /> AUTOMÁTICO · {tipo}
      </span>
      <div style={{
        background: 'linear-gradient(135deg, #fff, ' + C.bone + ')',
        border: `1px dashed ${esError ? C.red : C.teal}`,
        borderRadius: 10, padding: '7px 11px 5px', fontSize: 13, lineHeight: 1.4,
        color: C.text, maxWidth: '92%', alignSelf: 'center',
        boxShadow: `0 1px 3px ${esError ? 'rgba(226,75,74,.1)' : 'rgba(0,168,181,.10)'}`,
        opacity: esProgramada ? 0.7 : 1,
      }}>
        {esProgramada && (
          <div style={{ fontSize: 11, color: C.amber, marginBottom: 3, display:'flex', alignItems:'center', gap:4 }}>
            <Icon name="clock" size={11} /> Programada para {fmtHora(automation?.created_at)}
          </div>
        )}
        <div style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
          {msg.texto || automation?.template_params?.preview || `(template ${tipo})`}
        </div>
        {esError && (msg.estado_error || automation?.error_msg) && (
          <div style={{ fontSize: 10.5, color: C.redDark, marginTop: 4 }}>
            Falló: {msg.estado_error || automation?.error_msg}
          </div>
        )}
        <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2, textAlign: 'right',
                      display:'flex', justifyContent:'flex-end', alignItems:'center', gap:3 }}>
          {fmtHora(msg.sent_at || msg.created_at || automation?.created_at)}
          {msg.read_at && <Icon name="checks" size={11} color={C.teal} />}
          {!msg.read_at && msg.delivered_at && <Icon name="checks" size={11} color={C.muted} />}
          {!msg.delivered_at && msg.sent_at && <Icon name="check" size={11} color={C.muted} />}
        </div>
        {(esProgramada || esError) && (
          <div style={{ display:'flex', gap:5, marginTop: 5, flexWrap:'wrap' }}>
            {esProgramada && onAdelantar && (
              <button style={S.miniBtn} onClick={() => onAdelantar(automation)}>Adelantar</button>
            )}
            {esProgramada && onCancelar && (
              <button style={S.miniBtn} onClick={() => onCancelar(automation)}>Cancelar</button>
            )}
            {esError && onReintentar && (
              <button style={S.miniBtnPrimary} onClick={() => onReintentar(automation)}>Reintentar</button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const TIPO_ICON = {
  kit_sale_manana: 'truck', repartidor_saliendo: 'truck-delivery',
  entregado: 'package-check', csat_entrega: 'star',
  cobro_fallido: 'alert', pedido_confirmado: 'receipt',
  bienvenida_suscripcion: 'sparkles', cumpleanios_aviso: 'cake',
  recompra_30d: 'rotate', carrito_abandonado_4h: 'shopping-cart',
}

// ─────────── Timeline de automatizaciones del cliente ───────────
export function AutomationsTimeline({ clienteId, onAccion }) {
  const { items, loading } = useAutomatizacionesCliente(clienteId)
  const grupos = useMemo(() => {
    const g = {}
    for (const it of items) {
      const d = new Date(it.created_at)
      const dia = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      if (!g[dia]) g[dia] = []
      g[dia].push(it)
    }
    return g
  }, [items])

  if (loading) return <div style={{ padding: 20, color: C.muted, textAlign:'center' }}>Cargando…</div>
  if (!items.length) return <div style={{ padding: 20, color: C.muted, textAlign:'center' }}>Sin automatizaciones todavía.</div>

  return (
    <div style={{ background:'#fff' }}>
      {Object.entries(grupos).map(([dia, list]) => (
        <div key={dia}>
          <div style={{
            padding: '8px 14px 4px', fontSize: 10.5, fontWeight: 600,
            color: C.muted, background: C.bone, textTransform: 'uppercase', letterSpacing: 0.5,
          }}>{etiquetaDia(dia)}</div>
          {list.map(it => (
            <AutomationRow key={it.id} item={it} onAccion={onAccion} />
          ))}
        </div>
      ))}
    </div>
  )
}
function etiquetaDia(yyyymmdd) {
  const hoy = new Date()
  const ayer = new Date(); ayer.setDate(ayer.getDate()-1)
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  if (yyyymmdd === fmt(hoy)) return 'Hoy'
  if (yyyymmdd === fmt(ayer)) return 'Ayer'
  const d = new Date(yyyymmdd + 'T12:00')
  return d.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' })
}
function AutomationRow({ item, onAccion }) {
  const estado = item.estado || 'pendiente'
  const estyle = ESTADO_STYLE[estado] || ESTADO_STYLE.pendiente
  const tipoIcon = TIPO_ICON[item.tipo] || 'sparkles'
  const preview = item.template_params?.preview ||
    Object.values(item.template_params || {}).slice(0,2).join(' · ') ||
    `template: ${item.template_name}`

  return (
    <div style={{ display:'flex', gap:10, padding:'9px 14px', borderBottom:`0.5px solid ${C.line}` }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        background: estyle.bg, color: estyle.fg,
        display:'inline-flex', alignItems:'center', justifyContent:'center',
      }}>
        <Icon name={tipoIcon} size={14} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{item.tipo}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{fmtHora(item.enviado_at || item.created_at)}</div>
        </div>
        <div style={{ fontSize: 11.5, color: '#5b6068', marginTop: 2,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {preview}
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginTop: 3, display:'flex', gap:8, alignItems:'center' }}>
          <span style={{
            fontSize: 9.5, padding: '1px 6px', borderRadius: 3, fontWeight: 600,
            background: estyle.badgeBg, color: estyle.fg,
          }}>{estado}</span>
          {item.origen_tabla && <span>trigger: {item.origen_tabla}</span>}
        </div>
        {(estado === 'pendiente' || estado === 'programada' || estado === 'failed') && onAccion && (
          <div style={{ display:'flex', gap:4, marginTop: 4 }}>
            {estado !== 'failed' && <button style={S.miniBtn} onClick={() => onAccion('adelantar', item)}>Adelantar</button>}
            {estado !== 'failed' && <button style={S.miniBtn} onClick={() => onAccion('cancelar', item)}>Cancelar</button>}
            {estado === 'failed' && <button style={S.miniBtnPrimary} onClick={() => onAccion('reintentar', item)}>Reintentar</button>}
          </div>
        )}
      </div>
    </div>
  )
}
const ESTADO_STYLE = {
  pendiente:  { bg: 'rgba(186,117,23,.15)',  fg: '#854F0B', badgeBg: 'rgba(186,117,23,.18)' },
  programada: { bg: 'rgba(186,117,23,.15)',  fg: '#854F0B', badgeBg: 'rgba(186,117,23,.18)' },
  enviada:    { bg: 'rgba(0,168,181,.12)',   fg: '#0E7A82', badgeBg: 'rgba(0,168,181,.15)' },
  entregada:  { bg: 'rgba(0,168,181,.12)',   fg: '#0E7A82', badgeBg: 'rgba(0,168,181,.15)' },
  leida:      { bg: 'rgba(151,196,89,.18)',  fg: '#27500A', badgeBg: 'rgba(151,196,89,.20)' },
  respondida: { bg: 'rgba(151,196,89,.18)',  fg: '#27500A', badgeBg: 'rgba(151,196,89,.20)' },
  failed:     { bg: 'rgba(226,75,74,.12)',   fg: '#A32D2D', badgeBg: 'rgba(226,75,74,.15)' },
  no_enviada: { bg: 'rgba(226,75,74,.12)',   fg: '#A32D2D', badgeBg: 'rgba(226,75,74,.15)' },
}

// ─────────── Drawer Ficha 360 del cliente ───────────
// Fullscreen en mobile, slide-in derecho (380px) en desktop.
// Si no hay cliente vinculado: muestra info del contacto + acciones.
// Si hay cliente: trae suscripción, entregas, deuda, LTV, dirección, notas, tags.
export function CustomerDrawer({ open, onClose, conv, mobile = false, onConvActualizada }) {
  const [ficha, setFicha] = useState(null)
  const [entregas, setEntregas] = useState([])
  const [tab, setTab] = useState('info')
  const [tags, setTags] = useState(conv?.tags || [])
  const [notas, setNotas] = useState(conv?.notas_internas || '')
  const [guardado, setGuardado] = useState(null)
  const clienteId = conv?.cliente_id

  useEffect(() => { setTags(conv?.tags || []); setNotas(conv?.notas_internas || '') }, [conv?.id])

  useEffect(() => {
    if (!open || !clienteId) return
    supabase.from('clientes')
      .select(`
        id, alias, nombres, apellidos, telefono, kit_actual, estado, barrio,
        direccion, apto, lat, lng, fecha_alta,
        suscripcion:suscripciones!suscripciones_cliente_id_fkey(id, estado, plan, monto_mensual, proxima_entrega)
      `)
      .eq('id', clienteId)
      .single()
      .then(({ data }) => setFicha(data))
    supabase.from('entregas')
      .select('id, fecha_programada, estado, monto_total, ventana_horaria, kit_codigo')
      .eq('cliente_id', clienteId)
      .order('fecha_programada', { ascending: false })
      .limit(5)
      .then(({ data }) => setEntregas(data || []))
  }, [open, clienteId])

  async function guardarTags(nuevos) {
    setTags(nuevos)
    await supabase.from('conversations').update({ tags: nuevos, updated_at: new Date().toISOString() }).eq('id', conv.id)
    onConvActualizada?.({ ...conv, tags: nuevos })
  }
  async function guardarNotas() {
    await supabase.from('conversations').update({ notas_internas: notas, updated_at: new Date().toISOString() }).eq('id', conv.id)
    setGuardado('OK'); setTimeout(() => setGuardado(null), 1500)
    onConvActualizada?.({ ...conv, notas_internas: notas })
  }

  if (!open) return null
  const sub = Array.isArray(ficha?.suscripcion) ? ficha.suscripcion[0] : ficha?.suscripcion
  const ult = entregas[0]
  const totalFacturado = entregas.reduce((s, e) => s + (parseFloat(e.monto_total) || 0), 0)
  const nombre = ficha?.alias || [ficha?.nombres, ficha?.apellidos].filter(Boolean).join(' ') || conv?.nombre_visible || 'Cliente'
  const tel = ficha?.telefono || conv?.phone
  const canal = conv?.canal

  const wrap = mobile
    ? { position:'fixed', inset:0, background:'#fff', zIndex: 9999, display:'flex', flexDirection:'column', overflow:'hidden', height:'100dvh' }
    : { position:'fixed', top:0, right:0, bottom:0, width:380, background:'#fff', zIndex: 200, boxShadow:'-4px 0 18px rgba(15,31,61,.18)', display:'flex', flexDirection:'column' }

  return (
    <div style={wrap}>
      {/* Header navy con avatar + nombre */}
      <div style={{
        background: C.navy, color:'#fff',
        paddingTop: mobile ? 'calc(14px + env(safe-area-inset-top, 0px))' : 14,
        paddingRight: mobile ? 12 : 16, paddingBottom: 16, paddingLeft: mobile ? 12 : 16,
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, color:'rgba(255,255,255,.7)', fontWeight: 500 }}>Ficha del cliente</div>
          <button onClick={onClose} style={S.iconBtn}><Icon name="x" size={18} color="#fff" /></button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
          <div style={{ ...S.avBig, background: gradAvatar(hashStr(nombre)), border:'2px solid rgba(255,255,255,.2)' }}>
            {classNombre(nombre)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.1 }}>{nombre}</div>
            <div style={{ fontSize: 12.5, color:'rgba(255,255,255,.75)', marginTop: 2 }}>
              {tel ? '+' + String(tel).replace(/\D/g,'') : (conv?.ig_username ? '@' + conv.ig_username : '—')}
              {ficha?.barrio && ` · ${ficha.barrio}`}
            </div>
            {canal && (
              <div style={{ fontSize: 11, marginTop: 4, display:'inline-flex', alignItems:'center', gap: 3, background:'rgba(255,255,255,.15)', padding:'2px 7px', borderRadius: 999 }}>
                <Icon name={canal === 'whatsapp' ? 'brand-whatsapp' : 'brand-instagram'} size={10} />
                {canal === 'whatsapp' ? 'WhatsApp' : canal === 'instagram' ? 'Instagram' : canal}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap: 6, padding: '10px 12px', borderBottom: `0.5px solid ${C.line}` }}>
        <ActBtn icon="brand-whatsapp" label="WhatsApp" onClick={() => {
          const t = tel ? String(tel).replace(/\D/g,'') : null
          if (t) window.open(`https://wa.me/${t}`, '_blank')
        }} disabled={!tel} />
        <ActBtn icon="phone" label="Llamar" onClick={() => { if (tel) window.location.href = `tel:+${String(tel).replace(/\D/g,'')}` }} disabled={!tel} />
        <ActBtn icon="user" label="Ficha 360" onClick={() => { if (clienteId) window.open(`/admin/usuarios/${clienteId}`, '_blank') }} disabled={!clienteId} />
        <ActBtn icon="note" label="Nota" onClick={() => setTab('notas')} />
      </div>

      {/* Tags editables */}
      <div style={{ padding:'8px 12px 6px', borderBottom:`0.5px solid ${C.line}` }}>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, fontWeight: 600, letterSpacing: 0.4 }}>TAGS</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap: 4 }}>
          {tags.map(t => (
            <span key={t} style={{ ...S.tag, display:'inline-flex', alignItems:'center', gap: 3, padding:'2px 5px 2px 7px' }}>
              {t}
              <button onClick={() => guardarTags(tags.filter(x => x !== t))} style={{ background:'transparent', border:0, cursor:'pointer', color: C.tealDark, padding: 0, display:'flex' }}>
                <Icon name="x" size={10} />
              </button>
            </span>
          ))}
          <button onClick={() => {
            const nuevo = window.prompt('Agregar tag (ej: vip, churn, fuera-zona):')?.trim().toLowerCase()
            if (nuevo && !tags.includes(nuevo)) guardarTags([...tags, nuevo])
          }} style={{ ...S.tag, background:'transparent', border:`0.5px dashed ${C.line}`, color: C.muted, cursor:'pointer', display:'inline-flex', alignItems:'center', gap: 3 }}>
            <Icon name="plus" size={10} /> agregar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap: 18, padding: '6px 16px 0', borderBottom: `0.5px solid ${C.line}` }}>
        {[
          { v: 'info',             l: 'Datos',            ic: 'user' },
          { v: 'pedidos',          l: 'Pedidos',          ic: 'package' },
          { v: 'automatizaciones', l: 'Automáticos',      ic: 'sparkles' },
          { v: 'notas',            l: 'Notas',            ic: 'note' },
        ].map(t => (
          <button key={t.v} onClick={() => setTab(t.v)} style={{
            border: 'none', background:'transparent', cursor:'pointer',
            padding: '8px 0', fontSize: 12.5, display:'inline-flex', alignItems:'center', gap:4,
            color: tab === t.v ? C.navy : C.muted,
            fontWeight: tab === t.v ? 600 : 400,
            borderBottom: tab === t.v ? `2px solid ${C.teal}` : '2px solid transparent',
            whiteSpace:'nowrap',
          }}>
            <Icon name={t.ic} size={13} /> {t.l}
          </button>
        ))}
      </div>

      {/* Contenido tabs */}
      <div style={{ flex: 1, overflow:'auto' }}>
        {tab === 'info' && (
          !clienteId ? (
            <NoClienteEmpty conv={conv} />
          ) : (
            <div>
              {sub && (
                <InfoRow icon="package" k="Plan activo" v={sub.plan || ficha?.kit_actual || '—'} right={sub.monto_mensual ? `$${sub.monto_mensual}/mes` : null} accent={sub.estado === 'activa'} />
              )}
              <InfoRow icon="calendar" k="Próxima entrega" v={sub?.proxima_entrega ? new Date(sub.proxima_entrega).toLocaleDateString('es-UY', { weekday:'short', day:'numeric', month:'short' }) : '—'} right={ult?.ventana_horaria} />
              <InfoRow icon="user" k="Estado cliente" v={ficha?.estado || '—'} />
              <InfoRow icon="map-pin" k="Dirección" v={ficha?.direccion ? `${ficha.direccion}${ficha.apto ? ' ap ' + ficha.apto : ''}` : '—'} sub={ficha?.barrio} />
              <InfoRow icon="history" k="Cliente desde" v={ficha?.fecha_alta ? new Date(ficha.fecha_alta).toLocaleDateString('es-UY') : '—'} right={entregas.length ? `${entregas.length} entregas` : null} />
              {totalFacturado > 0 && (
                <InfoRow icon="credit-card" k="Total facturado" v={`$${Math.round(totalFacturado).toLocaleString('es-UY')}`} accent />
              )}
              {ficha?.lat && ficha?.lng && (
                <a href={`https://www.openstreetmap.org/?mlat=${ficha.lat}&mlon=${ficha.lng}#map=17/${ficha.lat}/${ficha.lng}`} target="_blank" rel="noreferrer" style={{ display:'block', textDecoration:'none' }}>
                  <div style={{ height: 90, margin:'8px 14px', borderRadius: 8, background: 'linear-gradient(135deg, #E6F1FB, #B5D4F4)', position:'relative', display:'flex', alignItems:'center', justifyContent:'center', backgroundImage: 'linear-gradient(0deg, rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
                    <Icon name="map-pin" size={24} color="#185FA5" />
                    <span style={{ position:'absolute', bottom: 4, right: 8, fontSize: 10, color: '#185FA5', fontWeight: 600 }}>Abrir mapa →</span>
                  </div>
                </a>
              )}
              <div style={{ padding: 12 }}>
                <a href={`/admin/usuarios/${clienteId}`} target="_blank" rel="noreferrer" style={S.linkBtn}>
                  Ver ficha 360 completa →
                </a>
              </div>
            </div>
          )
        )}

        {tab === 'pedidos' && (
          !clienteId ? <NoClienteEmpty conv={conv} /> :
          entregas.length === 0 ? <div style={{ padding: 20, color: C.muted, textAlign:'center', fontSize: 13 }}>Sin entregas todavía.</div> :
          <div>
            {entregas.map(e => (
              <div key={e.id} style={{ display:'flex', alignItems:'center', gap: 10, padding:'10px 14px', borderBottom:`0.5px solid ${C.line}` }}>
                <div style={{ width: 32, height: 32, borderRadius:'50%', background: estadoEntregaColor(e.estado).bg, color: estadoEntregaColor(e.estado).fg, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink: 0 }}>
                  <Icon name="package" size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{e.kit_codigo || 'Kit'}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {e.fecha_programada ? new Date(e.fecha_programada + 'T12:00').toLocaleDateString('es-UY', { day:'numeric', month:'short' }) : '—'}
                    {e.ventana_horaria && ` · ${e.ventana_horaria}`}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>${e.monto_total || 0}</div>
                  <div style={{ fontSize: 10, color: estadoEntregaColor(e.estado).fg, fontWeight: 600 }}>{e.estado}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'automatizaciones' && (
          !clienteId ? <NoClienteEmpty conv={conv} /> :
          <AutomationsTimeline clienteId={clienteId} onAccion={(accion, item) => console.log(accion, item)} />
        )}

        {tab === 'notas' && (
          <div style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.4 }}>NOTAS INTERNAS</div>
            <textarea
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Cumple Marta · perro chico, no usar lavandina concentrada · prefiere franja tarde…"
              rows={8}
              style={{ width:'100%', border: `0.5px solid ${C.line}`, borderRadius: 8, padding: 10, fontSize: 13, fontFamily:'inherit', color: C.text, resize:'vertical' }}
            />
            <div style={{ display:'flex', gap: 8, marginTop: 8, alignItems:'center' }}>
              <button onClick={guardarNotas} style={S.linkBtn}>Guardar notas</button>
              {guardado && <span style={{ fontSize: 11, color: '#27500A', fontWeight: 600 }}>✓ Guardado</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ActBtn({ icon, label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display:'flex', flexDirection:'column', alignItems:'center', gap: 3, padding: '8px 4px',
      background: disabled ? '#F1EFE8' : C.bone, borderRadius: 10, border: `0.5px solid ${C.line}`,
      cursor: disabled ? 'default' : 'pointer', fontFamily:'inherit',
      opacity: disabled ? 0.5 : 1,
    }}>
      <Icon name={icon} size={18} color={C.navy} />
      <span style={{ fontSize: 10, color: C.text }}>{label}</span>
    </button>
  )
}

function NoClienteEmpty({ conv }) {
  return (
    <div style={{ padding: 24, textAlign:'center', color: C.muted }}>
      <Icon name="user" size={40} color={C.muted} />
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginTop: 10 }}>Sin cliente vinculado</div>
      <div style={{ fontSize: 12, marginTop: 4, maxWidth: 240, margin: '4px auto 0' }}>
        Este chat viene del contacto <strong>{conv?.nombre_visible || conv?.phone}</strong> pero todavía no hay un cliente Limpack asociado.
      </div>
      <a href={`/admin/clientes?vincular=${conv?.id}`} target="_blank" rel="noreferrer" style={{ ...S.linkBtn, display:'inline-block', marginTop: 14, padding:'8px 16px' }}>
        Vincular a cliente →
      </a>
    </div>
  )
}

function estadoEntregaColor(e) {
  return {
    entregada: { bg:'rgba(151,196,89,.2)', fg:'#27500A' },
    en_ruta:   { bg:'rgba(0,168,181,.15)', fg:'#0E7A82' },
    programada:{ bg:'rgba(186,117,23,.15)', fg:'#854F0B' },
    pendiente_pago: { bg:'rgba(226,75,74,.12)', fg:'#A32D2D' },
    no_entregada: { bg:'rgba(226,75,74,.12)', fg:'#A32D2D' },
    cancelada: { bg:'rgba(100,116,139,.15)', fg:'#475569' },
  }[e] || { bg:'rgba(100,116,139,.15)', fg:'#475569' }
}
function InfoRow({ icon, k, v, right, sub, accent }) {
  return (
    <div style={{ display:'flex', gap:10, padding:'10px 14px', borderBottom:`0.5px solid ${C.line}`, alignItems:'center' }}>
      <Icon name={icon} size={16} color={accent ? C.teal : C.muted} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, letterSpacing: 0.3 }}>{k}</div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: accent ? 600 : 500 }}>{v || '—'}</div>
        {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{sub}</div>}
      </div>
      {right && <div style={{ fontSize: 12, color: accent ? C.tealDark : C.text, fontWeight: 600, textAlign:'right' }}>{right}</div>}
    </div>
  )
}
function hashStr(s='') { let h=0; for (let i=0;i<s.length;i++) h=(h*31+s.charCodeAt(i))|0; return Math.abs(h) }

// ─────────── Quick Actions Sheet (bottom sheet con 8 acciones + templates) ───────────
export function QuickActionsSheet({ open, onClose, onAccion, templates = [] }) {
  if (!open) return null
  const acciones = [
    { v: 'template',    l: 'Template',    ic: 'template' },
    { v: 'etiquetar',   l: 'Etiquetar',   ic: 'tag' },
    { v: 'agendar',     l: 'Agendar',     ic: 'calendar' },
    { v: 'ver-pedidos', l: 'Ver pedidos', ic: 'package' },
    { v: 'cfe',         l: 'Mandar CFE',  ic: 'receipt' },
    { v: 'pasar',       l: 'Asignar',     ic: 'user-plus' },
    { v: 'resolver',    l: 'Resolver',    ic: 'check-circle' },
    { v: 'sugerir-ia',  l: 'Sugerir IA',  ic: 'sparkles' },
  ]
  return (
    <div style={S.sheetBackdrop} onClick={onClose}>
      <div style={S.sheet} onClick={e => e.stopPropagation()}>
        <div style={S.sheetHandle} />
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: C.text }}>Acciones rápidas</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {acciones.map(a => (
            <button key={a.v} onClick={() => { onAccion?.(a.v); onClose() }} style={S.qaCard}>
              <Icon name={a.ic} size={18} color={C.navy} />
              <span style={{ fontSize: 9.5, color: C.text, textAlign:'center', lineHeight: 1.15 }}>{a.l}</span>
            </button>
          ))}
        </div>
        {templates.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: C.text }}>Templates</div>
            <div style={{ display:'flex', flexDirection:'column', gap: 5 }}>
              {templates.slice(0, 5).map(t => (
                <button key={t.name} onClick={() => { onAccion?.('template', t); onClose() }} style={S.tplCard}>
                  <div style={S.tplIc}><Icon name="template" size={14} color={C.tealDark} /></div>
                  <div style={{ flex:1, minWidth:0, textAlign:'left' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.name}</div>
                    <div style={{ fontSize: 10.5, color: C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {t.body_text || ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────── AI Suggestions bar (chips de respuestas sugeridas) ───────────
export function AISuggestionsBar({ conversationId, onPick }) {
  const [suggs, setSuggs] = useState([])
  const [loading, setLoading] = useState(false)

  async function generar() {
    if (!conversationId) return
    setLoading(true)
    try {
      const res = await sugerirRespuesta(conversationId)
      const arr = Array.isArray(res?.opciones) ? res.opciones : (res?.opcion ? [res.opcion] : [])
      setSuggs(arr.slice(0, 3))
    } catch (e) { console.warn('sugerirRespuesta', e) }
    setLoading(false)
  }

  return (
    <div style={{ padding: '6px 8px 4px', background: 'linear-gradient(180deg, transparent, rgba(0,168,181,.05))',
                  display:'flex', gap: 5, overflowX:'auto', alignItems:'center' }}>
      {suggs.length === 0 && !loading && (
        <button onClick={generar} style={S.aiChip}>
          <Icon name="sparkles" size={12} color={C.teal} /> Sugerir respuesta
        </button>
      )}
      {loading && <span style={{ fontSize: 11, color: C.muted, padding:'4px 10px' }}>Pensando…</span>}
      {suggs.map((s, i) => (
        <button key={i} onClick={() => onPick?.(s)} style={S.aiChip}>
          <Icon name="sparkles" size={12} color={C.teal} /> {s.length > 50 ? s.slice(0,48) + '…' : s}
        </button>
      ))}
      {suggs.length > 0 && (
        <button onClick={() => setSuggs([])} style={{ ...S.aiChip, border: 'none', background:'transparent', color: C.muted }}>
          <Icon name="x" size={11} />
        </button>
      )}
    </div>
  )
}

// ─────────── Icons inline SVG ───────────
export function Icon({ name, size = 16, color = 'currentColor' }) {
  const s = size
  const P = (d) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
  switch (name) {
    case 'arrow-left': return P(<path d="M19 12H5M12 19l-7-7 7-7"/>)
    case 'search': return P(<><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></>)
    case 'plus': return P(<><path d="M12 5v14"/><path d="M5 12h14"/></>)
    case 'x': return P(<><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>)
    case 'check': return P(<path d="M20 6 9 17l-5-5"/>)
    case 'checks': return P(<><path d="m1 12 5 5L17 6"/><path d="m9 17 9-11"/></>)
    case 'bell': return P(<><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></>)
    case 'bell-off': return P(<><path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5"/><path d="M17 17H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/><path d="M2 2l20 20"/></>)
    case 'send': return P(<path d="m22 2-7 20-4-9-9-4 20-7Z"/>)
    case 'microphone': return P(<><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><path d="M12 19v4"/></>)
    case 'mood-smile': return P(<><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01M15 9h.01"/></>)
    case 'paperclip': return P(<path d="m21 11-8.6 8.6a5 5 0 1 1-7-7L14 4a3.5 3.5 0 1 1 5 5L9.4 18.6a2 2 0 1 1-3-3l8-8"/>)
    case 'camera': return P(<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>)
    case 'bolt': return P(<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>)
    case 'sparkles': return P(<><path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4z"/><path d="M5 19l.6 1.4L7 21l-1.4.6L5 23l-.6-1.4L3 21l1.4-.6z"/><path d="M19 17l.6 1.4L21 19l-1.4.6L19 21l-.6-1.4L17 19l1.4-.6z"/></>)
    case 'tag': return P(<><path d="m20.59 13.41-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></>)
    case 'template': return P(<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></>)
    case 'calendar': return P(<><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>)
    case 'package': return P(<><path d="m16.5 9.4-9-5.19"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.27 6.96 8.73 5.05 8.73-5.05"/><path d="M12 22.08V12"/></>)
    case 'package-check': return P(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m16 11-4 4-2-2"/></>)
    case 'receipt': return P(<><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><path d="M8 7h8M8 11h8M8 15h6"/></>)
    case 'user': return P(<><circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></>)
    case 'user-plus': return P(<><circle cx="9" cy="7" r="4"/><path d="M3 21a6 6 0 0 1 12 0"/><path d="M19 8v6M22 11h-6"/></>)
    case 'users': return P(<><circle cx="9" cy="7" r="4"/><path d="M3 21a6 6 0 0 1 12 0"/><path d="M16 3a4 4 0 0 1 0 8"/><path d="M21 21a6 6 0 0 0-6-6"/></>)
    case 'check-circle': return P(<><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>)
    case 'alert': return P(<><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></>)
    case 'alert-triangle': return P(<><path d="m10.3 3.86 1.7-3 1.7 3L21 18H3z"/><path d="M12 9v4"/><path d="M12 17h.01"/></>)
    case 'truck': return P(<><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></>)
    case 'truck-delivery': return P(<><path d="M3 17h2v-7H3"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/><path d="M5 17h7M14 17V8h5l2 4v5h-2"/></>)
    case 'star': return P(<path d="m12 2 3.1 6.3L22 9.3l-5 4.9L18.2 22 12 18.5 5.8 22 7 14.2 2 9.3l6.9-1z"/>)
    case 'cake': return P(<><path d="M20 21V11a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v10"/><path d="M3 21h18"/><path d="M12 8V3M8 6l4-3 4 3"/></>)
    case 'rotate': return P(<><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></>)
    case 'shopping-cart': return P(<><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/></>)
    case 'map-pin': return P(<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>)
    case 'history': return P(<><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/></>)
    case 'note': return P(<><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/></>)
    case 'phone': return P(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.91.7 2.81a2 2 0 0 1-.45 2.11L8 10a16 16 0 0 0 6 6l1.36-1.36a2 2 0 0 1 2.11-.45c.9.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>)
    case 'info-circle': return P(<><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>)
    case 'dots-vertical': return P(<><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></>)
    case 'chart-bar': return P(<><path d="M3 21V3"/><path d="M3 17h4v4M3 12h7v9M3 7h13v14"/></>)
    case 'broadcast': return P(<><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.48"/><path d="M7.76 16.24a6 6 0 0 1 0-8.48"/><path d="M20.07 4.93a10 10 0 0 1 0 14.14"/><path d="M3.93 19.07a10 10 0 0 1 0-14.14"/></>)
    case 'message-circle': return P(<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.7 8.7 0 0 1-3.9-.9L3 21l1.9-5.1A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 17 0z"/>)
    case 'address-book': return P(<><path d="M4 19.5a2.5 2.5 0 0 1 2.5-2.5H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><circle cx="12" cy="10" r="2"/><path d="M9 16a3 3 0 0 1 6 0"/></>)
    case 'edit': return P(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 1 1 3 3L12 15l-4 1 1-4z"/></>)
    case 'pin': return P(<><path d="m12 17 5-5"/><path d="M15 9 9 3 3 9l6 6 6-6z"/><path d="M5 21l3.5-3.5"/></>)
    case 'volume-off': return P(<><path d="m11 5-6 4H2v6h3l6 4z"/><path d="M22 9 16 15M16 9l6 6"/></>)
    case 'corner-up-left': return P(<><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 5 5v6"/></>)
    case 'clock': return P(<><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>)
    case 'filter': return P(<path d="M22 3H2l8 9.5V19l4 2v-8.5z"/>)
    case 'robot': return P(<><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><path d="M8 16h.01M16 16h.01"/></>)
    case 'credit-card': return P(<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></>)
    case 'brand-whatsapp': return P(<><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.7 8.7 0 0 1-3.9-.9L3 21l1.9-5.1A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 17 0z"/></>)
    case 'brand-instagram': return P(<><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5"/></>)
    default: return P(<circle cx="12" cy="12" r="1"/>)
  }
}

// ─────────── Estilos compartidos ───────────
const S = {
  iconBtn: { width: 32, height: 32, borderRadius: '50%', display:'inline-flex', alignItems:'center', justifyContent:'center', border: 'none', background: 'transparent', cursor:'pointer' },
  miniBtn: { fontSize: 10, padding:'3px 8px', background:'#fff', border:`0.5px solid ${C.line}`, borderRadius: 999, color: C.text, cursor:'pointer', fontFamily:'inherit' },
  miniBtnPrimary: { fontSize: 10, padding:'3px 8px', background: C.navy, border:`0.5px solid ${C.navy}`, borderRadius: 999, color: '#fff', cursor:'pointer', fontFamily:'inherit' },
  avBig: { width: 70, height: 70, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 600, fontSize: 22 },
  tag: { fontSize: 10, padding:'2px 7px', borderRadius: 3, background: 'rgba(0,168,181,.12)', color: C.tealDark, fontWeight: 600 },
  linkBtn: { display:'block', textAlign:'center', padding:'8px', background: C.navy, color:'#fff', borderRadius: 8, textDecoration:'none', fontSize: 12.5, fontWeight: 600 },
  sheetBackdrop: { position:'fixed', inset: 0, background:'rgba(15,31,61,.55)', zIndex: 300, display:'flex', alignItems:'flex-end' },
  sheet: { width:'100%', background:'#fff', borderRadius:'18px 18px 0 0', padding:'14px 14px 24px', maxHeight: '85%', overflowY:'auto', boxShadow:'0 -4px 18px rgba(15,31,61,.18)' },
  sheetHandle: { width: 36, height: 4, background:'rgba(15,31,61,.18)', borderRadius: 2, margin:'0 auto 10px' },
  qaCard: { display:'flex', flexDirection:'column', alignItems:'center', gap: 4, padding:'8px 4px', background: C.bone, borderRadius: 10, border:`0.5px solid ${C.line}`, cursor:'pointer', fontFamily:'inherit' },
  tplCard: { display:'flex', alignItems:'center', gap: 8, padding:'7px 9px', background: C.bone, borderRadius: 8, border:`0.5px solid ${C.line}`, cursor:'pointer', fontFamily:'inherit', textAlign:'left' },
  tplIc: { width: 26, height: 26, borderRadius: 6, background:'rgba(0,168,181,.15)', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink: 0 },
  aiChip: { fontSize: 11, padding:'4px 10px', background:'#fff', border: `0.5px solid ${C.teal}`, color: C.tealDark, borderRadius: 999, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap: 4, fontFamily:'inherit', cursor:'pointer', flexShrink: 0 },
}
